import { ArrowLeft, CheckCircle2, CircleAlert, Send, Ban, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/common/loader";
import {
  cancelSubmission,
  deleteSubmission,
  transmitSubmission,
} from "@/features/efiling/api";
import { SubmissionStatusBadge } from "@/features/efiling/components/submission-status-badge";
import { useSubmission } from "@/features/efiling/hooks/use-submission";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ClientSubmissionDetailPage() {
  const { client } = useActiveClient();
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { submission, loading, error, refetch } = useSubmission(
    client.id,
    submissionId ?? "",
  );
  const [accessCode, setAccessCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return <Loader className="mt-10" />;
  }
  if (error || !submission) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="text-muted-foreground text-sm">{error ?? "Submission not found."}</p>
        <Button asChild variant="outline">
          <Link to={`/client/${client.id}/efiling`}>Back to e-Filing</Link>
        </Button>
      </div>
    );
  }

  const isDraft = submission.status === "DRAFT";
  const isQueued = submission.status === "QUEUED";
  const needsAccessCode = submission.channel === "DOC_UPLOAD_TOOL";
  const creds = submission.practitionerCredentialsReady;

  async function handleTransmit() {
    setBusy(true);
    try {
      await transmitSubmission(client.id, submission!.id, accessCode || undefined);
      toast.success("Submission queued for the IRS connector.");
      refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't transmit.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    setBusy(true);
    try {
      await cancelSubmission(client.id, submission!.id);
      toast.success("Submission cancelled.");
      refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't cancel.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    setBusy(true);
    try {
      await deleteSubmission(client.id, submission!.id);
      toast.success("Draft deleted.");
      navigate(`/client/${client.id}/efiling`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete.");
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to={`/client/${client.id}/efiling`}
          className="text-muted-foreground hover:text-primary mb-3 inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="size-4" /> Back to e-Filing
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-foreground text-2xl font-bold">{submission.channelDisplay}</h1>
          <SubmissionStatusBadge status={submission.status} label={submission.statusDisplay} />
        </div>
      </div>

      {submission.status === "REJECTED" && submission.errorMessage && (
        <div className="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border px-4 py-3 text-sm">
          <CircleAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            Rejected{submission.irsAckCode ? ` (${submission.irsAckCode})` : ""}: {submission.errorMessage}
          </span>
        </div>
      )}

      <Card className="flex-col gap-4 p-6">
        <Detail label="Assigned practitioner" value={submission.practitionerName} />
        <div>
          <p className="text-muted-foreground text-xs font-medium uppercase">Credentials on file</p>
          <div className="mt-1 flex gap-4 text-sm">
            <CredFlag ok={creds.efin} label="EFIN" />
            <CredFlag ok={creds.caf} label="CAF" />
          </div>
        </div>
        <Detail label="IRS confirmation" value={submission.irsConfirmationNumber || "—"} />
        <Detail
          label="Acknowledgement"
          value={submission.irsAckStatus || "—"}
        />
        <Detail label="Submitted" value={formatDateTime(submission.submittedAt)} />
        <Detail label="Transmitted" value={formatDateTime(submission.transmittedAt)} />
      </Card>

      <Card className="flex-col gap-3 p-6">
        <h2 className="text-foreground text-lg font-semibold">Items ({submission.items.length})</h2>
        <ul className="flex flex-col gap-1 text-sm">
          {submission.items.map((item) => (
            <li key={item.id} className="text-muted-foreground flex items-center gap-2">
              <span className="text-foreground">{item.label}</span>
              {item.formNumber && <span className="text-xs">· Form {item.formNumber}</span>}
            </li>
          ))}
        </ul>
      </Card>

      {(isDraft || isQueued) && (
        <Card className="flex-col gap-3 p-6">
          <h2 className="text-foreground text-lg font-semibold">Actions</h2>
          {isDraft && needsAccessCode && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="access-code">IRS notice access code</Label>
              <Input
                id="access-code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="From the IRS notice"
                className="max-w-xs"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {isDraft && (
              <>
                <Button onClick={handleTransmit} disabled={busy}>
                  <Send className="size-4" /> Transmit
                </Button>
                <Button variant="outline" onClick={handleDelete} disabled={busy}>
                  <Trash2 className="size-4" /> Delete draft
                </Button>
              </>
            )}
            {isQueued && (
              <Button variant="outline" onClick={handleCancel} disabled={busy}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card className="flex-col gap-3 p-6">
        <h2 className="text-foreground text-lg font-semibold">History</h2>
        <ol className="flex flex-col gap-3">
          {submission.events.map((ev) => (
            <li key={ev.id} className="flex items-start gap-3 text-sm">
              <CheckCircle2 className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <div>
                <p className="text-foreground font-medium">{ev.eventTypeDisplay}</p>
                {ev.detail && <p className="text-muted-foreground text-xs">{ev.detail}</p>}
                <p className="text-muted-foreground text-xs">{formatDateTime(ev.createdAt)}</p>
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <p className="text-foreground text-sm">{value}</p>
    </div>
  );
}

function CredFlag({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={ok ? "text-success" : "text-destructive"}>
      {label}: {ok ? "on file" : "missing"}
    </span>
  );
}
